/**
 * Formats a phone number for human-readable display.
 *
 * Preferred output: `(123) 456-7890` (US 10-digit).
 *
 * Fallbacks (in priority order):
 * - 11 digits starting with `1`  → `(123) 456-7890` (country code stripped)
 * - 11 digits not starting with 1 → `+X (XXX) XXX-XXXX`
 * - Anything else (too few/too many digits, letters, etc.) → returns the
 *   trimmed raw string so we never mangle unexpected input.
 */
export function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (!trimmed) return '';

  const digits = trimmed.replace(/\D/g, '');

  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  if (digits.length === 11) {
    const country = digits.slice(0, 1);
    const area = digits.slice(1, 4);
    const prefix = digits.slice(4, 7);
    const line = digits.slice(7);
    if (country === '1') {
      return `(${area}) ${prefix}-${line}`;
    }
    return `+${country} (${area}) ${prefix}-${line}`;
  }

  // Unexpected lengths — return the raw value rather than mangling it.
  return trimmed;
}
